import { useFieldArray, UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";
import { AssignableMediaTypes, PlayerMediaSelectRaw } from "@/pages/player/form/PlayerMediaSelectRaw";
import { useEffect, useState } from "react";

type Props = {
  form: UseFormReturn<{
    contentList: {
      contentId?: number;
      contentGroupId?: number;
    }[];
  }>;
  visibleTypes: AssignableMediaTypes[];
  defaultScreenSizeGuid?: string;
  maxItems?: number;
};

export const AssignedContentList: React.FC<Props> = ({ form, visibleTypes, defaultScreenSizeGuid, maxItems = 100 }) => {
  const { t } = useTranslation();
  const [firstType, setFirstType] = useState<AssignableMediaTypes>();

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: "contentList",
  });

  useEffect(() => {
    if (fields.length === 0) {
      setFirstType(undefined);
    }
  }, [fields.length, setFirstType]);

  return (
    <div className="border border-dashed border-gray-300 rounded-md xl:p-4 p-2">
      <div className="flex justify-between items-center">
        <FormLabel>{t("content.contentList")}</FormLabel>
        {maxItems && fields.length < maxItems && (
          <Button
            type="button"
            variant="outline"
            onClick={() => append({ contentId: undefined, contentGroupId: undefined })}
          >
            <PlusIcon className="w-2 h-2" />
            {t("common.addNewEntity", { entity: t("entities.content") })}
          </Button>
        )}
      </div>
      {fields.map((field, index) => (
        <div key={field.id} className="flex gap-2 items-end mt-4">
          <FormField
            control={form.control}
            name={`contentList.${index}`}
            render={({ field }) => (
              <FormItem>
                <PlayerMediaSelectRaw
                  value={{
                    contentId: field.value?.contentId,
                    contentGroupId: field.value?.contentGroupId,
                  }}
                  onChange={(value, type) => {
                    setFirstType(type);
                    if (type === "content") {
                      update(index, { contentGroupId: undefined, contentId: value.contentId });
                    }
                    if (type === "contentGroup") {
                      update(index, { contentGroupId: value.contentGroupId, contentId: undefined });
                    }
                  }}
                  visibleTypes={visibleTypes}
                  onClear={() => {
                    remove(index);
                  }}
                  enabledTypes={firstType ? [firstType] : ["content", "contentGroup"]}
                  defaultScreenSizeGuid={defaultScreenSizeGuid}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      ))}
    </div>
  );
};
